(ns machtfit.product-form.handlers
  (:require [ajax.core :refer [GET POST]]
            [clojure.string :as str]
            [machtfit.employee-rules :refer [aggregate-rules expand-rules]]
            [machtfit.product-form.inhibit :as inhibit]
            [machtfit.product-form.what :as what]
            [machtfit.product-form.when :as when]
            [machtfit.utils :as utils]
            [medley.core :refer [filter-vals map-vals]]
            [re-frame.core :as rf]))

(defn- employee-rule-default [current-db]
  (let [user (-> current-db :user)
        offer-admin? (-> user :roles (utils/in? "Angebotsadmin"))
        machtfit-admin? (-> user :roles (utils/in? "Machtfit-Admin"))
        partner (-> current-db :ui :partner)]
    (cond
      (:is_supplier partner) []
      (and offer-admin? (not machtfit-admin?)) (-> user :managed-employees (aggregate-rules :place-of-business :places-of-business))
      :else [{:condition {:company (:company_id partner)}}])))

(defn- load-product-defaults [current-db]
  (let [partner-product? (-> current-db :options :partner-product?)
        partner-name (-> current-db :ui :partner :name)
        supplier-commission (-> current-db :ui :supplier :commission-rate)]
    (-> current-db
        (assoc :product {:state :new
                         :what {:p20-certified? partner-product?
                                :sorting-number 100
                                :source-type "form"
                                :bookable? true
                                :wellhub-promotion? false
                                :offer-layers {:enabled? false}}
                         :how {:extra-info-fields [{}]
                               :needs-access-code? false}
                         :how-much {:price-given? true
                                    :vat :germany-full
                                    :commission supplier-commission}
                         :for-whom {:audience-age #{:age15-17 :age18-49 :age50-69 :age70}
                                    :allowed-employees (employee-rule-default current-db)}
                         :when {:schedules [{:open? true}]
                                :slots [{:group 0}]}
                         :where {:address {:country "Deutschland"}
                                 :is-open-despite-corona? true}})
        (assoc-in [:product :who :supplier-name] (when-not partner-product? partner-name))
        (assoc-in [:product :how :registration-needed?] partner-product?)
        (assoc-in [:product :how :registration-type] (when partner-product? :machtfit)))))

(rf/reg-event-db ::load-product-defaults
  (fn [db _]
    (load-product-defaults db)))

(rf/reg-event-db ::load-product
  (fn [db [_ product]]
    (let [publish-by-default? (and (not= (product :state) :archived)
                                   (-> db :options :product-needs-verification?)
                                   (not (-> db :options :can-publish?)))]
      (-> db
          (update :product #(utils/deep-merge % product))
          (assoc-in [:ui :unit-duration-set-manually?] (not (str/blank? (-> product :when :unit-duration))))
          (assoc-in [:ui :access-codes-warning-threshold-set-manually?] (some? (-> product :how :access-codes-warning-threshold)))
          (assoc-in [:ui :image-set-manually?] (some? (-> product :what :image-id)))
          (assoc-in [:ui :publish?] publish-by-default?)))))

(rf/reg-event-db ::save-suppliers
  (fn [db [_ suppliers]]
    (let [current-executing-supplier (get-in db [:product :who :executing-supplier])]
      (-> db
                     ;; Add current executing supplier to suppliers list if not already included
          (assoc :suppliers (if (and current-executing-supplier (some #(= current-executing-supplier %) suppliers))
                              suppliers
                              (conj suppliers current-executing-supplier)))))))

(rf/reg-event-db ::fetch-suppliers [rf/trim-v]
  (fn [current-db]
    (let [is-admin? (-> current-db
                        :user
                        :roles
                        (utils/in? "Machtfit-Admin"))
          is-reselling-product? (get-in
                                 current-db
                                 [:options :is-reselling-product?])
          suppliers-needed? (and is-admin? is-reselling-product?)]
      (if suppliers-needed?
        (do
          (GET
           (get-in current-db [:io :supplier-lookup-url])
           {:response-format :json,
            :keywords? true,
            :handler #(rf/dispatch [::save-suppliers (% :results)]),
            :params {:page_limit -1, :only_with_contract "true"}})
          current-db)
        current-db))))

(rf/reg-event-db ::set-ui
  (fn [db [_ ui]]
    (-> db
        (assoc :ui ui)
        (update-in [:ui :vat-options] #(map (fn [[k v]] [(keyword k) v]) %)))))

(rf/reg-event-db ::set-io
  (fn [db [_ io]]
    (assoc db :io io)))

(rf/reg-event-db ::set-csrf-token
  (fn [db [_ csrf-token]]
    (assoc-in db [:io :csrf-token] csrf-token)))

(defn- set-number-of-appointments [product]
  (let [has-schedule? (-> product :when :has-schedule?)
        sessions-per-signup (-> product :when :sessions-per-signup)
        cohesion (-> product :when :cohesion)
        is-progressive? (and has-schedule? (= sessions-per-signup :multiple-sessions) (= cohesion :progressive))
        num-appointments? (-> product :how-much :number-of-appointments str str/blank? not)
        appointments-in-first-schedule (-> product :when :schedules first :appointments count)]
    (if (and is-progressive? (not num-appointments?) (> appointments-in-first-schedule 0))
      (assoc-in product [:how-much :number-of-appointments] appointments-in-first-schedule)
      product)))

(defn- clean-up-when [{:keys [slots schedules] :as when-data}]
  (let [deleted-slots-with-participants (into []
                                              (comp (filter :delete-with-participants?)
                                                    (map :slot-id))
                                              slots)
        slots-to-keep (into []
                            (comp (remove :delete-with-participants?)
                                  (map #(dissoc % :group
                                                :can-delete?
                                                :has-participants?
                                                :delete-with-participants?))
                                  (remove empty?))
                            slots)
        deleted-schedules-with-participants (into []
                                                  (comp (filter #(and (:deleted? %)
                                                                      (not (:can-delete? %))))
                                                        (map :schedule-id))
                                                  schedules)
        schedules-to-keep (->> schedules
                               when/only-actual-schedules
                               (map #(select-keys % [:schedule-id :appointments :registration-deadline])))]
    (-> when-data
        (assoc :slots slots-to-keep
               :schedules schedules-to-keep)
        (assoc-in [:delete-with-participants :slot-ids] deleted-slots-with-participants)
        (assoc-in [:delete-with-participants :schedule-ids] deleted-schedules-with-participants))))

(defn- clean-up-promotions [promotions]
  (remove #(and (not (:id %))
                (:deleted? %))
          promotions))

(defn- db->product [db]
  (let [extract-choice #(when (= :set (:choice %)) (:value %))
        partner-product? (get-in db [:options :partner-product?])]
    (-> db
        :product
        (cond-> (not partner-product?)
          (dissoc :resale-info))
        (update :what dissoc :image)
        ;; AI search keywords and their tagging status are read-only display data;
        ;; never send them back on save.
        (update :what dissoc :ai-search-keywords :ai-tag-status)
        (update-in [:what :p20-certified?] #(and partner-product? %))
        (update-in [:what :attachments] what/process-raw-attachments)
        (update-in [:when :weekdays] #(->> %
                                           (filter-vals :takes-place?)
                                           (map-vals :times)
                                           (map-vals (fn [times] (remove nil? times)))
                                           (filter-vals (fn [times] (> (count times) 0)))
                                           (map-vals set)))
        (update-in [:when :registration-deadline-relative] extract-choice)
        (update-in [:for-whom :min-participants] extract-choice)
        (update-in [:for-whom :max-participants] extract-choice)
        (update-in [:for-whom :allowed-employees] #(-> %
                                                       (expand-rules :places-of-business :place-of-business)
                                                       (expand-rules :employee-groups :employee-group)))
        (update :when clean-up-when)
        (update :resale-info (fn [{:keys [resale-product?] :as resale-info}]
                               (if-not resale-product?
                                 (select-keys resale-info [:resale-product?])
                                 resale-info)))
        (update-in [:how-much :max-bookings] extract-choice)
        (update-in [:how-much :subsidies] #(expand-rules % :places-of-business :place-of-business))
        (update :promotions #(clean-up-promotions %))
        (set-number-of-appointments))))

(rf/reg-event-db ::save [rf/trim-v]
  (fn [current-db]
    (if-not (inhibit/inhibited? :all (-> current-db :ui :save-inhibitors))
      (inhibit/set-save-inhibitor
       (do
         (POST
          (get-in current-db [:io :save-url])
          {:params (clj->js {:product (db->product current-db),
                             :partner-id (-> current-db :ui :partner :id)
                             :publish? (-> current-db :ui :publish?)})
           :response-format :json
           :headers {"X-CSRFToken" (-> current-db :io :csrf-token)}
           :keywords? true
           :handler #(rf/dispatch [::save-success %])
           :error-handler #(rf/dispatch [::save-error %])})
         current-db)
       :all)
      current-db)))

(defn- redirect [current-db url]
  (aset js/window.top.location "href" (str url (get-in current-db [:io :querystring]))))

(rf/reg-event-db ::cancel [rf/trim-v]
  (fn [current-db]
    (redirect current-db (-> current-db :ui :cancel-url))
    current-db))

(rf/reg-event-db ::save-success [rf/trim-v]
  (fn [current-db [result]]
    (let [new-files (keep :file-object (get-in current-db [:product :what :attachments]))
          upload-url (:attachments-upload-url result)
          form-data (js/FormData.)]
      (doseq [file new-files]
        (.append form-data "files[]" file (.-name file)))

      (-> (js/fetch upload-url
                    (clj->js {:method "POST"
                              :body form-data
                              :headers {"X-CSRFToken" (-> current-db :io :csrf-token)}
                              :credentials "same-origin"}))
          (.then (fn [response]
                   (cond
                     (= 400 (.-status response))
                     {:error "Upload fehlgeschlagen"}

                     (.-ok response)
                     (.json response)

                     :else (throw (js/Error. "Network response was not ok.")))))
          (.then (fn [data]
                   (if (:error data)
                     (rf/dispatch [::save-error (:error data)])
                     (rf/dispatch [::save-finish (:redirect-url result)]))))
          (.catch (fn [error]
                    (js/console.error "Upload failed" error))))
      current-db)))

(rf/reg-event-db ::save-finish [rf/trim-v]
  (fn [current-db [redirect-url]]
    (when-not (nil? redirect-url)
      (redirect current-db redirect-url))
    (inhibit/unset-save-inhibitor current-db :all)
    current-db))

(rf/reg-event-db ::save-error [rf/trim-v]
  (fn [current-db [result]]
    (let [errors (:errors (:response result))]
      (utils/scroll-up)
      (-> current-db
          (assoc :errors (if (empty? errors)
                           ["Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut. Wenn der Fehler weiterhin auftritt, kontaktieren Sie bitte machtfit."]
                           errors))
          (inhibit/unset-save-inhibitor :all)))))

(rf/reg-event-db :toggle
  (fn [db [_ path]]
    (update-in db path not)))

;; Scroll the body such that a button that was used to reveal new content above the button stays at
;; the same position, under the mouse cursor, allowing the button to be clicked again easily.
(rf/reg-event-db ::do-scroll-down
  (fn [db _]
    (let [new-scroll-height (aget js/window "document" "body" "scrollHeight")
          height-delta (- new-scroll-height (get-in db [:ui :scroll-height]))
          new-scroll-top (+ (aget js/window "document" "body" "scrollTop") height-delta)]
      (aset js/window "document" "body" "scrollTop" new-scroll-top)
      (update db :ui dissoc :scroll-height))))
